import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faSpinnerThird, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { getLocalUserId } from 'src/features/auth/lib/getLocalUser';
import { useListUserMemberships } from 'src/features/profile/api/useListUserMemberships';
import { ActiveStay, ActiveStayPeriod } from 'src/features/stay/types';
import ValidationForm from 'src/features/validations/components/forms/ValidationForm';
import { validationFormSchema, ValidationFormSchemaValues } from 'src/features/validations/utils/formSchema';
import { useListValidationTypes } from 'src/features/validationTypes/api/useListValidationTypes';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';

interface ValidationFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedStayPeriod?: ActiveStayPeriod;
    selectedStay?: ActiveStay;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: ValidationFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

export default function ValidationFormModal<TData>({
    selectedStayPeriod,
    selectedStay,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: ValidationFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { parkingId } = useParking();

    const onError = useOnError();

    const user = getLocalUserId();

    const { data: userMemberships } = useListUserMemberships({
        userId: user,
        parking: parkingId,
    });

    const cloudUser = userMemberships && userMemberships[0].sp_cloud_user_id;

    const { data: typesList } = useListValidationTypes({
        currency: selectedStay?.currency,
        parkingId,
        cloud_user: cloudUser,
        is_active: true,
        config: { enabled: !!cloudUser },
    });

    const defaultValues = React.useMemo(() => {
        const validatyionType = typesList?.results?.length === 1 ? typesList?.results[0] : null;
        return {
            validator_account: validatyionType?.validator_account ?? '',
            type: validatyionType?.id ?? '',
            active_stay_period: selectedStayPeriod?.id,
            visitor: selectedStay?.main_identifier?.value,
        };
    }, [selectedStayPeriod, selectedStay, typesList]);

    const form = useForm<ValidationFormSchemaValues>({
        resolver: zodResolver(validationFormSchema),
        defaultValues,
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(defaultValues);
    }, [isModalOpen, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: ValidationFormSchemaValues) =>
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            ),
        [mutate, form, onError],
    );
    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={className}>
                        <FontAwesomeIcon icon={faPlus} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ValidationForm form={form} onSubmit={onSubmit} types={typesList} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={
                            isPending ||
                            (selectedStayPeriod && !form.formState.isDirty && typesList?.results?.length !== 1)
                        }
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
